import Vue from 'vue';
import {
  get,
  set,
  merge,
  isEqual,
  isEmptyValue,
  isObject,
  clearObject,
  cloneDeep,
  isArray,
  isString,
  upperFirst,
  isNil,
  isNotEmptyObject,
  omitObjectKeysByObject,
  omitObjectValuesByObject,
} from 'Utils/common';
import validations from '@acretrader/validations';
import {
  getFieldsRulesByParams,
  getFieldErrorMessageByValidation,
  getFieldRuleByServerResponse,
} from '@acretrader/validations/dist/helpers';
import { getServerErrorByCode } from 'Config/server-error-messages';
import BaseModel from './BaseModel';

export default class BaseFormModel extends BaseModel {
  constructor(data) {
    super();

    // Array of field names
    this.$definedFields = [];
    // Form params and validation
    this.$params = {};
    this._validationsRulesByParams = {};
    this._validationsRules = {};
    // Validation rules dynamic generated by model for vuelidate
    this.$validationsRules = {};
    // Validation results of vuelidate
    this.$v = {};
    // Loaders
    // TODO: rename $loadingParams => $paramsLoading, $loadingData => $dataLoading
    this.$loadingParams = false;
    this.$loadingData = false;
    this.$loadingSendFieldAll = [];
    this.$dataLoadingPromise = null;
    this.$paramsLoadingPromise = null;
    this.$dataLoaded = false;
    this.$paramsLoadingPromise = null;
    this.$paramsLoaded = false;
    // Current data on the sever
    Vue.set(this, '$serverData', {});
    // Is failed during getting form params
    this.$isFormParamsFailed = false;
    // General validation error
    this.$generalValidationError = null;
    // Is reset fields on success
    this.$resetOnSuccess = true;
    // If true, will send all data not only changed
    this.$allDataRequired = false;
    // If false, will send data even if no changes
    this.$changedDataRequired = true;
    // Empty fields will be added to changed data and send
    this.$sendEmptyFields = false;
    // Scroll page to top on send success
    this.$scrollTopOnSuccess = true;
    // Scroll to error field on validation error
    this.$scrollToErrorField = true;
    // Is scroll to validation error fields if validation rules is changes
    this.$scrollToErrorFieldOnRulesChange = true;
    // Timeout to send fields for autosaving
    this.$sendFieldsTimeout = 2000;
    // Default data to set on init and exclude from changed data
    this.$set('$defaultData', {});
    if (data) {
      this._setData(data);
    }
  }

  get $potentialFields() {
    const potentialFields = [];
    Object.keys(this)
      .forEach((fieldName) => {
        if (fieldName[0] !== '$' && fieldName[0] !== '_') {
          potentialFields.push(fieldName);
        }
      });
    return potentialFields;
  }

  get $fields() {
    return this.$isFormParamsFailed ? this.$potentialFields : this.$definedFields;
  }

  get $data() {
    const values = {};
    this.$fields.forEach((fieldName) => {
      values[fieldName] = this[fieldName];
    });
    return values;
  }

  get $changedDataRaw() {
    const values = {};
    this.$fields.forEach((fieldName) => {
      const value = get(this.$data, fieldName);
      const serverValue = get(this.$serverData, fieldName);
      if (value !== serverValue) values[fieldName] = value;
    });
    return values;
  }

  get $changedData() {
    const values = {};
    this.$fields.forEach((fieldName) => {
      const value = this.$data[fieldName];
      const serverValue = this.$serverData[fieldName];
      if (
        // for checkbox: empty server data means false for checkbox, so it's the same
        !(value === false && isEmptyValue(serverValue))
        // check with server data if field changed
        && !isEqual(value, serverValue)
        // Empty fields is not for changed data if it not required
        && (!isEmptyValue(value) || (!isEmptyValue(serverValue) && this.$sendEmptyFields))
      ) {
        // If value is object, clear it of empty values
        values[fieldName] = isObject(value) ? clearObject(value) : value;
      }
    });
    return values;
  }

  get $hasChangedDataByUser() {
    // filter default data to get not saved default data
    const filteredDefault = omitObjectKeysByObject(this.$defaultData, this.$serverData);
    // filter changed data by not saved default data
    const filteredChanged = omitObjectValuesByObject(this.$changedData, filteredDefault);
    return isNotEmptyObject(filteredChanged);
  }

  get $hasChangedData() {
    return isNotEmptyObject(this.$changedData);
  }

  get $loadingPre() {
    return this.$loadingData || this.$loadingParams;
  }

  get $loading() {
    return this.$loadingPre || this.$loadingSend;
  }

  get $loadingSendField() {
    return Boolean(this.$loadingSendFieldAll.length);
  }

  get $hasServerData() {
    return Boolean(Object.keys(this.$serverData).length);
  }

  get $vInvalid() {
    return this.$v && this.$v.$invalid;
  }

  get $vDirty() {
    return this.$v && this.$v.$dirty;
  }

  get $vError() {
    return this.$v && this.$v.$error;
  }

  get __error__() {
    return this.$generalValidationError;
  }

  set __error__(error) {
    this.$set('$generalValidationError', error);
  }

  _getFormParams() {
    if (!this.$url) return Promise.reject(new Error('Model is not ready to get params: there is no URL'));
    this.$loadingParams = true;
    this.$paramsLoadingPromise = this.$httpClient.options(this.$path)
      .then(({ data }) => {
        this._setParams(data);
        this.$loadingParams = false;
        this.$paramsLoaded = true;
        return data;
      })
      .catch(() => {
        this.$loadingParams = false;
        this.$isFormParamsFailed = true;
      });
    return this.$paramsLoadingPromise;
  }

  _getData(setFields) {
    if (!this.$url) return Promise.reject(new Error('Model is not ready to get data: there is no URL'));
    this.$loadingData = true;
    this.$dataLoadingPromise = this.$httpClient.get(this.$getDataPath)
      .then(({ data }) => {
        this._setData(data);
        if (setFields) this._setFields(data);
        this.$loadingData = false;
        this.$dataLoaded = true;
        return data;
      })
      .catch(() => {
        this.$loadingData = false;
      });
    return this.$dataLoadingPromise;
  }

  _getFieldType(fieldName, params = this.$params) {
    return get(params, `[${fieldName}].type`);
  }

  _getFieldInitialValue(fieldName, params = this.$params) {
    switch (this._getFieldType(fieldName, params)) {
      case 'boolean':
      case 'bool':
      case 'checkbox':
        return false;
      case 'nested':
        return {};
      default:
        return '';
    }
  }

  _getRequestData() {
    return this.$allDataRequired ? this.$data : this.$changedData;
  }

  _generateValidationRules() {
    // pass model as context to get access to root model in custom validator
    return getFieldsRulesByParams.call(this, this.$params);
  }

  // set default model values. Handle nested objects
  $setDefaultData(data = this.$defaultData, model = this) {
    Object.entries(data).forEach(([key, value]) => {
      if (isObject(value)) {
        if (model[key] === undefined) this.$set(key, {}, model);
        this.$setDefaultData(value, model[key]);
      } else if (model[key] === undefined || model[key] === '') {
        this.$set(key, value, model);
      }
    });
  }

  _setParams(params) {
    if (!isObject(params)) {
      this.$isFormParamsFailed = true;
      return;
    }
    this.$params = params;
    this._setFields(params);
    this._setValidations();
  }

  _setData(data) {
    if (!data) return;
    // If data is nested $serverData will be changed on form changed
    this.$serverData = cloneDeep(data);
    this.$setAll(data);
  }

  _setFields(params) {
    this.$definedFields = isObject(params) ? Object.keys(params) : params;
    this._setInitialValues(params);
  }

  _setInitialValues(params = this.$params, model = this, currentData = model) {
    Object.keys(params).forEach((field) => {
      const initialValue = currentData[field] === undefined
        ? this._getFieldInitialValue(field, params)
        : currentData[field];
      Vue.set(model, field, initialValue);
      // If initial value is object, means it has sub fields
      if (isObject(initialValue)) {
        const subParams = get(params, `${field}.schema`, {});
        this._setInitialValues(subParams, initialValue);
      }
    });
  }

  _getGeneralValidationError(errors) {
    let message = '';
    const serverErrorByCode = getServerErrorByCode(errors.code);
    if (serverErrorByCode) {
      message = serverErrorByCode;
    } else if (errors.__error__) {
      const { __error__ } = errors;
      if (isArray(__error__)) {
        message = __error__.map(er => upperFirst(er)).join(', ');
      } else {
        message = __error__.statusDesc || JSON.stringify(__error__).replace(/"/g, '');
      }
    } else if (errors.error || errors.result || errors.message || isString(errors)) {
      message = upperFirst(errors.error || errors.result || errors.message || errors);
    }
    return message;
  }

  _setGeneralValidationError(errors) {
    const message = this._getGeneralValidationError(errors);
    Vue.set(this, '$generalValidationError', message);
  }

  $vTouch() {
    if (this.$v && this.$v.$touch) this.$v.$touch();
  }

  $vReset() {
    if (this.$v && this.$v.$reset) this.$v.$reset();
  }

  $validate() {
    if (this.$v && this.$v.$invalid) {
      this.$vTouch();
      throw this.$messageErrors.validation_error;
    }
    // If new data required and no changed data return error
    if (!this.$hasChangedData && this.$changedDataRequired) {
      throw this.$messageErrors.no_changed_data;
    }
  }

  _setValidations(isGenerateByParams = true) {
    // pass model as context to get access to root model in custom validator
    if (isGenerateByParams) this._validationsRulesByParams = this._generateValidationRules();
    this._validationsRulesAll = merge({}, this._validationsRulesByParams, this._validationsRules);
    // this was change because of error when rule need to be changes on fly
    // if (isNotEmptyObject(this.$v)) {
    //   this.$v.$addRules(this._validationsRulesAll);
    // } else {
    this.$set('$v', validations(this._validationsRulesAll, this));
    // }
  }

  _setFieldsLoading(fields, status) {
    const _fields = Array.isArray(fields) ? fields : [fields];
    const filtered = this.$loadingSendFieldAll.filter(f => !_fields.includes(f));
    this.$loadingSendFieldAll = status ? filtered.concat(_fields) : filtered;
  }

  _addRulesByServerResponse(errors, data) {
    this._setGeneralValidationError(errors);
    Object.keys(errors)
      .forEach((fieldName) => {
        if (
          fieldName === '__error__'
          || fieldName === 'result'
          || fieldName === 'error'
          || !Array.isArray(errors[fieldName])
        ) return;
        // get current field value
        const value = get(data, fieldName);
        // get error message from server response. Expected message as array
        const errorMessage = errors[fieldName].join(' ');
        // get field validator base on notEql validator
        const validator = getFieldRuleByServerResponse(fieldName, value, errorMessage);
        // create rule with uniq value base on value
        const rule = { [`notEql-${value}`]: validator };
        // creat rules object which corresponds to the structure of the model
        const rules = set({}, fieldName, rule);
        // add new validation rules
        this.$addValidationRules(rules);
      });
  }

  _beforeSend(ignoreValidation) {
    this.$sendAttempts += 1;

    if (ignoreValidation !== true) this.$validate();
    this.$loadingSend = true;
    // If new data required send only new data otherwise all data
    this.$requestData = this._getRequestData();
    this.$generalValidationError = null;
    return true;
  }

  _onSendSuccess(response) {
    const promise = super._onSendSuccess(response);
    if (!this.$resetOnSuccess || this.$hasServerData) {
      if (this.$allDataRequired) this.$setServerData(this.$requestData);
      else this.$updateServerData(this.$requestData);
    } else if (this.$resetOnSuccess) {
      this.$reset();
    }
    return promise;
  }

  _onSendError(error) {
    super._onSendError(error);
    const errors = get(error, 'response.data', {});
    this._addRulesByServerResponse(errors, this.$requestData);
    this.$vTouch();
    return error;
  }

  _onSendAlways() {
    super._onSendAlways();
    this.$requestData = null;
  }

  $reset(params = this.$params, model = this, data = {}) {
    this._setInitialValues(params, model, data);
    this.$vReset();
  }

  $resetToServerData() {
    this._setInitialValues(this.$params, this, cloneDeep(this.$serverData));
  }

  $resetToInitialValues(params = this.$params, model = this, data = {}) {
    this._setInitialValues(params, model, data);
  }

  /**
   * Filter provided data related to current model values and form params
   * @param data {Object} - data to set into model
   * @param emptyFieldsOnly {Boolean} - set field only if it empty in model
   * @param model {Object} - model where to set values
   * @param params {Object} - fields params
   * @returns {{}}
   */
  $getPreparedDataToDefinedFields(data, emptyFieldsOnly, model = this, params = this.$params) {
    if (!isObject(data)) return {};
    const readyData = {};
    Object.keys(params).forEach((field) => {
      const value = data[field];
      if (isObject(value)) {
        readyData[field] = this.$getPreparedDataToDefinedFields(
          value,
          emptyFieldsOnly,
          model[field] || {},
          (params[field]?.schema || {}),
        );
      } else if (value !== undefined && (!emptyFieldsOnly || !model[field])) {
        readyData[field] = value;
      }
    });
    return readyData;
  }

  /**
   * Set data from passed object to model.
   * Data filter by exist fields from form params
   * If second param emptyFieldsOnly are set to true, data will be set only into fields with empty data.
   * @param data {Object} - data to set into model
   * @param emptyFieldsOnly {Boolean} - set only empty fields in model
   * @param model {Object} - model where to set values
   */
  $setAllDefinedFields(data, emptyFieldsOnly = false, model = this) {
    const readyData = this.$getPreparedDataToDefinedFields(data, emptyFieldsOnly, model);
    this.$setAll(readyData);
  }

  $setData(data) {
    this._setData(data);
  }

  $setServerData(data) {
    Vue.set(this, '$serverData', data);
  }

  $updateServerData(data) {
    Vue.set(this, '$serverData', merge({}, this.$serverData, data));
  }

  $updateData(data) {
    this.$updateServerData(data);
    this.$setAll(data);
  }

  $addValidationRules(rules) {
    merge(this._validationsRules, rules);
    this._setValidations(false);
  }

  /**
   * Merge passed param to current params and recalculation validation rules
   * @param params
   */
  $addParams(params) {
    merge(this.$params, params);
    this._setValidations();
  }

  isFieldInvalid(fieldName) {
    const fieldV = get(this.$v, fieldName, {});
    return Boolean(fieldV.$invalid);
  }

  hasFieldError(fieldName) {
    return this.$v[fieldName] && this.$v[fieldName].$error;
  }

  getFieldError(fieldName) {
    const error = getFieldErrorMessageByValidation(get(this.$v, fieldName));
    return error || undefined;
  }

  _onFieldSendSuccess(response, request) { // eslint-disable-line no-unused-vars
    return true;
  }

  /**
   * Set timeout to send changes fields
   * @param fieldNames {Array|String} - single field name or set of field names
   */
  sendField(fieldNames) {
    this._fieldsToSend = this._fieldsToSend || new Set();
    if (Array.isArray(fieldNames)) {
      fieldNames.forEach(field => this._fieldsToSend.add(field));
    } else {
      this._fieldsToSend.add(fieldNames);
    }
    clearTimeout(this._sendFieldsTimeoutId);
    this._sendFieldsTimeoutId = setTimeout(this.sendFieldsNow.bind(this), this.$sendFieldsTimeout);
  }

  /**
   * Send changes fields. Using for auto save forms
   * @returns {Q.Promise<unknown>|Promise<void>}
   */
  sendFieldsNow(fieldNames) {
    clearTimeout(this._sendFieldsTimeoutId);
    if (!this.$hasChangedData) return Promise.resolve();
    let fields = [];
    if (fieldNames) fields = fields.concat(fieldNames);
    if (this._fieldsToSend) {
      fields = fields.concat(Array.from(this._fieldsToSend));
      this._fieldsToSend.clear();
    }
    if (!fields || !fields.length) return Promise.resolve();
    // Pass validation for fields
    const fieldsV = fields.map(fieldName => get(this.$v, fieldName));
    if (fieldsV.some(fieldV => (!fieldV || fieldV.$invalid))) {
      fieldsV.forEach(fieldV => fieldV && fieldV.$touch());
      // Return is invalidate
      return Promise.resolve(this.$messageErrors.validation_error);
    }
    // Get changed values
    const requestData = {};
    fields.forEach((fieldName) => {
      set(requestData, fieldName, get(this.$changedDataRaw, fieldName));
    });
    // Return if no at least one value
    if (!Object.values(requestData).some(val => !isNil(val))) {
      return Promise.resolve(this.$messageErrors.no_changed_data);
    }
    this._setFieldsLoading(fields, true);
    return this.$httpClient.request(
      this.$method,
      this.$path,
      requestData,
    )
      .then((response) => {
        this.$updateServerData(requestData);
        this._setFieldsLoading(fields, false);
        this._onFieldSendSuccess(response, requestData);
        return response;
      })
      .catch((error) => {
        this._setFieldsLoading(fields, false);
        throw error;
      });
  }
}
